import {
  useEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Reveal-on-scroll wrapper. Adds `.is-visible` (see index.css `.reveal`) the
 * first time the element enters the viewport. `delay` staggers grouped children.
 * Renders as `as` (default div) so it stays semantic.
 */
export function Reveal({
  children,
  as: Tag = 'div',
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  as?: ElementType;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}

/**
 * Counts from 0 to `end` once the host element scrolls into view. Returns the
 * ref to attach and the current display value (string, preserving any suffix).
 */
export function useCountUp<T extends HTMLElement = HTMLElement>(
  end: number,
  opts: { duration?: number; suffix?: string; decimals?: number } = {},
) {
  const { duration = 1400, suffix = '', decimals = 0 } = opts;
  const ref = useRef<T | null>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      setValue(end);
      return;
    }
    let raf = 0;
    let start = 0;
    const step = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(end * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            raf = requestAnimationFrame(step);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [end, duration]);

  return { ref, display: `${value.toFixed(decimals)}${suffix}` };
}

/** Parse a stat string like "4.8", "420+", "60+" into a number + suffix. */
export function parseStat(raw: string): { value: number; suffix: string; decimals: number } {
  const m = raw.match(/^([\d.]+)(.*)$/);
  if (!m) return { value: 0, suffix: raw, decimals: 0 };
  const num = parseFloat(m[1]!);
  return { value: num, suffix: m[2] ?? '', decimals: m[1]!.includes('.') ? 1 : 0 };
}
